import {z} from "zod";
import {zLocationId, zNormalizedStringToNull, zToNull} from "app/utils/validator";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonList,
  IonSelectOption,
  IonTitle,
  IonToolbar
} from "@ionic/react";
import {HomeBackButton} from "app/employee/menu/HomeBackButton";
import {MenuButton} from "app/employee/menu/MenuButton";
import {searchOutline, trashBinOutline} from "ionicons/icons";
import {useTranslation} from "react-i18next";
import {RhfIonInput} from "app/employee/rhf/RhfIonInput";
import {RhfFstIonSelect} from "app/employee/rhf/RhfFstIonSelect";
import {useMemo} from "react";
import {TicketStatus} from "app/gql/graphqlSchema";
import {setTicketFilter, useTicketFilterStore} from "app/employee/state/ticket/store";
import {shallow} from "zustand/shallow";
import {useEmployeeLocations} from "app/employee/state/bootstrapEmployee/selectors";
import {getLocationText} from "app/employee/utils";

interface Props {
  onApply: () => void;
}

const NO_LOCATION_KEY = "__no_location__";

const formSchema = z.object({
  statuses: z.array(z.nativeEnum(TicketStatus)).nullable(),
  locationIds: z.array(zLocationId.nullable()).nullable(),
  query: zToNull.or(zNormalizedStringToNull)
});

type FormData = z.infer<typeof formSchema>;

export const TicketFilter = ({onApply}: Props) => {
  const {t} = useTranslation();

  const locations = useEmployeeLocations();

  const filter = useTicketFilterStore(
    (state) => ({
      statuses: state.statuses,
      query: state.query,
      locationIds: state.locationIds
    }),
    shallow
  );

  const statusOptions = useMemo(
    () => [
      {label: t("ticket.status.TODO"), value: TicketStatus.Todo},
      {label: t("ticket.status.IN_PROGRESS"), value: TicketStatus.InProgress},
      {label: t("ticket.status.DONE"), value: TicketStatus.Done}
    ],
    [t]
  );

  const values = useMemo(
    () =>
      ({
        statuses: filter.statuses,
        query: filter.query,
        locationIds: filter.locationIds
      }) satisfies FormData,
    [filter.query, filter.statuses, filter.locationIds]
  );

  const {
    control,
    formState: {isValid, isSubmitting},
    handleSubmit
  } = useForm<FormData>({
    values,
    resolver: zodResolver(formSchema)
  });

  const applyFilter = (data: FormData) => {
    setTicketFilter({
      statuses: data.statuses,
      query: data.query,
      locationIds: data.locationIds ?? []
    });
    onApply();
  };

  const clearFilter = () => {
    setTicketFilter({statuses: null, query: null, locationIds: []});
    onApply();
  };

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <HomeBackButton />
          <IonTitle>{t("ticket.filter.title")}</IonTitle>
          <MenuButton />
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <IonList>
          <IonItem lines="full">
            <RhfIonInput
              clearInput
              label={t("ticket.filter.text")}
              labelPlacement="stacked"
              placeholder={t("ticket.filter.text-placeholder")}
              rhf={{control, name: "query"}}
            />
          </IonItem>

          <IonItem lines="full">
            <RhfFstIonSelect
              label={t("ticket.filter.location")}
              labelPlacement="stacked"
              multiple
              placeholder={t("g.all")}
              rhf={{control, name: "locationIds"}}
            >
              <IonSelectOption key={NO_LOCATION_KEY} value={null}>
                {t("ticket.filter.no-location")}
              </IonSelectOption>
              {locations.map((l) => (
                <IonSelectOption key={l.id.locationId} value={l.id}>
                  {getLocationText(l)}
                </IonSelectOption>
              ))}
            </RhfFstIonSelect>
          </IonItem>

          <IonItem lines="full">
            <RhfFstIonSelect
              label={t("ticket.filter.status")}
              labelPlacement="stacked"
              multiple
              placeholder={t("g.all")}
              rhf={{control, name: "statuses"}}
            >
              {statusOptions.map((opt) => (
                <IonSelectOption key={opt.value} value={opt.value}>
                  {opt.label}
                </IonSelectOption>
              ))}
            </RhfFstIonSelect>
          </IonItem>
        </IonList>
      </IonContent>
      <IonFooter>
        <IonToolbar>
          <IonButtons slot="primary">
            <IonButton color="primary" fill="outline" onClick={clearFilter}>
              <IonIcon icon={trashBinOutline} slot="start" />
              {t("g.clear")}
            </IonButton>
            <IonButton
              color="primary"
              disabled={isSubmitting || !isValid}
              fill="solid"
              onClick={handleSubmit(applyFilter)}
            >
              <IonIcon icon={searchOutline} slot="start" />
              {t("g.filter")}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </>
  );
};
